import { transformRoutes } from '@shared/utils';

const routeDefs = {
  purchaseAgreements: {
    path: '/purchase-agreements',
  },
  activateConditions: {
    path: '/activate-conditions',
  },
  activeMarkupCondition: {
    path: '/active-markup-condition',
  },
  customerMarkupConditions: {
    path: '/customer-markup-conditions',
  },
  customerAssortments: {
    path: '/customer-assortments',
  },
  conditions: {
    path: '/conditions',
  },
  draftConditions: {
    path: '/draft-conditions',
  },
  priceSuggestion: {
    path: '/price-suggestions',
    sub: {
      validatePrices: {
        path: '/validate',
      },
      itemPrices: {
        path: '/for-item',
      },
      filterContext: {
        path: '/filter-context',
      },
    },
  },
  assortmentTags: {
    path: '/assortment-tags',
    sub: {
      tags: { path: '/' },
      v2: { path: '/v2' },
      tagId: {
        path: '/:tagId',
        sub: {
          assortments: {
            path: '/assortment',
            sub: {
              assortment: {
                path: '/:assortmentId',
                sub: {
                  items: {
                    path: '/items',
                    sub: {
                      supplierItem: {
                        path: '/:supplierItemId',
                      },
                    },
                  },
                },
              },
            },
          },
        },
      },
    },
  },
  supplierItemsInsightReport: {
    path: '/insight/mg-item-insights',
  },
  assortmentPrices: {
    path: '/assortment-prices',
    sub: {
      assortment: {
        path: '/:assortmentId',
        sub: {
          calculation: {
            path: '/calculations',
            sub: {
              mgItem: {
                path: '/:mgItemNumber',
              },
            },
          },
          filterContext: {
            path: '/filter-context',
          },
          supplier: {
            path: '/supplier',
            sub: {
              supplierNumber: {
                path: '/:supplierNumber',
                sub: { filterContext: { path: '/filter-context' } },
              },
            },
          },
        },
      },
    },
  },
  assortments: {
    path: '/assortments',
    sub: {
      items: { path: '/items' },
      assortment: {
        path: '/:assortmentId',
        sub: {
          items: {
            path: '/items',
            sub: { expire: { path: '/expire' } },
          },
          centralAssortments: {
            path: '/central-assortments',
            sub: { filters: { path: '/filters' } },
          },
          agreementPricesComparison: { path: '/agreement-prices-comparison' },
          affectedSupplierItems: { path: '/affected-supplier-items' },
          conditions: {
            path: '/conditions',
            sub: {
              filters: { path: '/filter-context' },
            },
          },
          priceSuggestionWithCount: { path: '/price-suggestion-item-count' },
          priceRoundingRules: {
            path: '/price-rounding',
            sub: {
              ruleSets: {
                path: '/rule-sets',
              },
            },
          },
          memberCampaigns: {
            path: '/member-campaigns',
            sub: {
              campaign: {
                path: '/:campaignId',
                sub: {
                  enrollment: {
                    path: '/enrollment',
                  },
                },
              },
            },
          },
        },
      },
    },
  },
  assortmentConfig: {
    path: '/assortment-config',
    sub: {
      assortments: {
        path: '/assortment',
        sub: {
          assortment: {
            path: '/:assortmentId',
          },
        },
      },
      brands: {
        path: '/brand',
        sub: {
          brand: {
            path: '/:brandId',
            sub: {
              templates: {
                path: '/sftp-templates',
              },
            },
          },
        },
      },
      dataOwners: {
        path: '/data-owner',
        sub: {
          dataOwner: {
            path: '/:dataOwnerId',
            sub: {
              assortment: {
                path: '/assortment',
              },
            },
          },
        },
      },
    },
  },
  priceRounding: {
    path: '/price-rounding',
    sub: {
      simulation: { path: '/simulate' },
      ruleSets: {
        path: '/rule-sets',
        sub: { ruleSet: { path: '/:ruleSetId' } },
      },
      worstCaseRounding: { path: '/worst-case-rounding' },
    },
  },
  brandAssortments: {
    path: '/brand-assortments',
    sub: {
      assortment: {
        path: '/:assortmentId',
        sub: { items: { path: '/items' }, filters: { path: '/filters' } },
      },
    },
  },
  brands: {
    path: '/brands',
    sub: {
      brand: {
        path: '/:brandId',
        sub: {
          myAssortments: { path: '/my-assortments' },
        },
      },
    },
  },
  competitorPrices: {
    path: '/competitor-prices',
    sub: {
      assortment: {
        path: '/assortment',
        sub: {
          assortmentId: {
            path: '/:assortmentId',
            sub: {
              mgItem: { path: '/mg-item', sub: { mgItemNumber: { path: '/:mgItemNumber' } } },
            },
          },
        },
      },
    },
  },
  priceControl: {
    path: '/price-control',
    sub: {
      assortment: {
        path: '/assortment',
        sub: {
          assortmentId: {
            path: '/:assortmentId',
            sub: {
              supplier: {
                path: '/supplier',
                sub: {
                  supplierNumber: {
                    path: '/:supplierNumber',
                    sub: { filterContext: { path: '/filter-context' } },
                  },
                },
              },
              events: {
                path: '/events',
                sub: { memberEventId: { path: '/:memberEventId' } },
              },
              eventActions: {
                path: '/event-actions',
                sub: { validate: { path: '/validate' } },
              },
            },
          },
        },
      },
    },
  },
  mgItems: {
    path: '/mg-items',
    sub: {
      mgItemNumber: {
        path: '/:mgItemNumber',
        sub: {
          marketing: { path: '/marketing' },
          json: { path: '/json' },
          assortments: { path: '/assortments' },
          itemCard: {
            path: '/item-card',
            sub: {
              core: { path: '/core' },
              documentation: { path: '/documentation' },
              assortmentTags: { path: '/assortment-tags' },
            },
          },
        },
      },
      supplierItems: { path: '/supplier-items' },
    },
  },
  mgSupplierItems: {
    path: '/mg-supplier-items',
    sub: {
      supplierItem: {
        path: '/:supplierItemId',
        sub: {
          agreementPrices: {
            path: '/agreement-prices',
          },
        },
      },
      lookup: {
        path: '/lookup',
        sub: {
          assortmentItem: {
            path: '/assortment-items-upload',
          },
          campaignItem: {
            path: '/campaign-item',
          },
          memberCampaignItem: {
            path: '/member-campaign-item',
          },
        },
      },
    },
  },
  suppliers: {
    path: '/suppliers',
    sub: {
      supplier: {
        path: '/:supplierId',
        sub: {
          agreementPrices: { path: '/agreement-prices' },
          productGroups: { path: '/product-groups' },
          discountGroups: { path: '/discount-groups' },
          configuration: { path: '/configuration' },
          nonStockItemSupplier: {
            path: '/non-stock-item-supplier',
            sub: { assortment: { path: '/:assortmentId' } },
          },
          items: {
            path: '/items',
            sub: {
              item: {
                path: '/:mgItemNumber',
                sub: {
                  climateData: { path: '/climate-data' },
                },
              },
            },
          },
        },
      },
    },
  },
  orgUnits: {
    path: '/org-units',
    sub: {
      orgUnit: {
        path: '/:orgUnitId',
        sub: {
          competitors: { path: '/competitors' },
          assortments: { path: '/assortments' },
        },
      },
    },
  },
  monitoringAssortments: {
    path: '/monitoring-assortments',
    sub: {
      assortment: {
        path: '/:assortmentId',
        sub: {
          items: { path: '/items' },
        },
      },
      validateItemCodes: { path: '/validate-item-codes' },
    },
  },
  retailers: {
    path: '/retailers',
  },
  users: {
    path: '/users',
    sub: {
      me: { path: '/me' },
    },
  },
  productGroups: {
    path: '/product-groups',
  },
  discountGroups: {
    path: '/discount-groups',
  },
  export: {
    path: '/totalfil',
    sub: {
      recalculate: { path: '/request-recalculation/:assortmentId' },
      summary: {
        path: '/summary/:assortmentId',
      },
      downloadZip: {
        path: '/download/:assortmentId',
      },
      downloadCSV: {
        path: '/download-csv/:assortmentId',
      },
    },
  },
  globalItemUnits: {
    path: '/global-item-units',
  },
  dashboard: {
    path: '/dashboard',
    sub: {
      newAssortmentItems: { path: '/new-assortment-items' },
      updatedAgreementPrices: {
        path: '/updated-agreement-prices',
      },
      competitorPricesCount: {
        path: '/updated-competitor-prices-count',
      },
      centralAssortment: { path: '/central-assortment' },
    },
  },
  campaigns: {
    path: '/campaigns',
    sub: {
      campaign: {
        path: '/:campaignId',
        sub: {
          items: {
            path: '/items',
            sub: {
              campaignItem: {
                path: '/:campaignItemId',
              },
            },
          },
          distribute: {
            path: '/distribute',
          },
          brands: {
            path: '/brands',
          },
        },
      },
      items: {
        path: '/items',
        sub: {
          campaignItem: {
            path: '/:campaignItemId',
          },
        },
      },
      searchItems: {
        path: '/searchItems',
      },
      validateCampaignItems: {
        path: '/validate-items',
      },
    },
  },
  memberCampaigns: {
    path: '/member-campaigns',
    sub: {
      memberCampaign: {
        path: '/:campaignId',
        sub: {
          items: {
            path: '/items',
            sub: {
              campaignItem: {
                path: '/:campaignItemId',
                sub: {
                  salesPriceOverride: { path: '/sales-price-override' },
                },
              },
            },
          },
        },
      },
    },
  },
  localCampaigns: {
    path: '/local-campaigns',
    sub: {
      localCampaign: {
        path: '/:campaignId',
        sub: {
          items: {
            path: '/items',
            sub: {
              campaignItem: {
                path: '/:campaignItemId',
              },
            },
          },
        },
      },
      items: {
        path: '/items',
        sub: {
          campaignItem: {
            path: '/:campaignItemId',
          },
        },
      },
    },
  },
} as const;

export const mpeApi = transformRoutes(routeDefs, '/api/v1');
